import { useCallback, useState } from 'react';
import { Box, Button } from '@mui/material';
import {
  AdminService,
  MembershipService,
  InvitationService,
  useApiEffect,
  useApiGetter,
  useRoutes,
} from '@localstack/services';

import { AdminPermission, CreateInvitationRequest, UserRole } from '@localstack/types';

import { Add as AddIcon, Mail as MailIcon } from '@mui/icons-material';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { AddMemberDialog, InviteMemberModal, MembersAndLicenses } from '~/components/MembersAndLicenses';
import { AppRoute } from '~/config';

export interface MembersAndLicensesProps {
  orgId: string;
}

export const MembersAndLicensesPage = ({ orgId }: MembersAndLicensesProps): JSX.Element => {
  const { goto } = useRoutes();
  const { can } = useAuthProvider();
  const disableInteraction = !can(AdminPermission.WORKSPACES_WRITE);

  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showInviteMemberDialog, setShowInviteMemberDialog] = useState(false);

  const {
    data: membersList,
    isLoading: areMembersLoading,
    mutate: mutateMembers,
  } = useApiGetter(MembershipService, 'adminListMembers', [orgId], { defaultValue: [] });

  const { data: subscriptionsList, isLoading: areSubscriptionsLoading } = useApiGetter(
    AdminService,
    'listOrganizationSubscriptions',
    [orgId],
    { defaultValue: [] },
  );

  const { data: licenseAssignmentsList, isLoading: areLicenseAssignmentsLoading } = useApiGetter(
    AdminService,
    'listOrganizationLicenseAssignments',
    [orgId],
    {
      defaultValue: [],
      enable: !areSubscriptionsLoading && !areMembersLoading,
    },
  );

  const {
    data: invitationsList,
    isLoading: areInvitationsLoading,
    mutate: mutateInvitations,
  } = useApiGetter(InvitationService, 'adminListInvitations', [orgId], { defaultValue: [] });

  const { setLicenseAssignments } = useApiEffect(AdminService, ['setLicenseAssignments'], {
    rethrow: true,
  });

  const { adminUpdateMembership, adminDeleteMembership, adminCreateMembership } = useApiEffect(
    MembershipService,
    ['adminUpdateMembership', 'adminDeleteMembership', 'adminCreateMembership'],
    { rethrow: true },
  );

  const { adminDeleteInvitation, adminCreateInvitation } = useApiEffect(
    InvitationService,
    ['adminDeleteInvitation', 'adminCreateInvitation'],
    { rethrow: true },
  );

  const handleAddMember = useCallback(
    async (userId: string) => {
      try {
        const newMembership = await adminCreateMembership(orgId, { user_id: userId });
        await mutateMembers((current) => [newMembership, ...(current ?? [])], { revalidate: false });
        setShowAddMemberDialog(false);
      } catch {
        // already handled
      }
    },
    [adminCreateMembership, orgId, mutateMembers],
  );

  const handleCreateInvitation = useCallback(
    async (invitation: CreateInvitationRequest) => {
      const newInvitation = await adminCreateInvitation(orgId, invitation);
      await mutateInvitations((current) => [newInvitation, ...(current ?? [])], { revalidate: false });
    },
    [adminCreateInvitation, orgId, mutateInvitations],
  );

  return (
    <>
      {showAddMemberDialog && (
        <AddMemberDialog onClose={() => setShowAddMemberDialog(false)} onConfirm={handleAddMember} />
      )}
      {showInviteMemberDialog && (
        <InviteMemberModal onClose={() => setShowInviteMemberDialog(false)} onConfirm={handleCreateInvitation} />
      )}

      <Box height="100%">
        <MembersAndLicenses
          subscriptions={subscriptionsList}
          areSubscriptionsLoading={areSubscriptionsLoading}
          licenseAssignments={licenseAssignmentsList}
          areLicenseAssignmentsLoading={areLicenseAssignmentsLoading}
          members={membersList}
          invitations={invitationsList}
          isMembersLoading={areMembersLoading}
          isInvitationsLoading={areInvitationsLoading}
          canUpdateMembers={!disableInteraction}
          canDeleteMembers={!disableInteraction}
          deleteMember={(id: string) => adminDeleteMembership(orgId, id)}
          deleteInvitation={(id: string) => adminDeleteInvitation(orgId, id)}
          updateMembershipRole={(userId: string, role: UserRole) => adminUpdateMembership(orgId, userId, { role })}
          updateMembershipPermissions={(userId: string, permissions: string[]) =>
            adminUpdateMembership(orgId, userId, { permissions })
          }
          setLicenseAssignmentsForUser={(userId: string, subscriptionIds: string[]) =>
            setLicenseAssignments(orgId, userId, {
              subscription_ids: subscriptionIds,
            })
          }
          onUserClick={(userId: string) => goto(AppRoute.ADMIN_ACCOUNT, { id: userId })}
        >
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowAddMemberDialog(true)}
              disabled={disableInteraction}
            >
              Add member
            </Button>

            <Button
              size="small"
              startIcon={<MailIcon />}
              onClick={() => setShowInviteMemberDialog(true)}
              disabled={disableInteraction}
            >
              Invite member
            </Button>
          </Box>
        </MembersAndLicenses>
      </Box>
    </>
  );
};
